import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Plus, Search } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ACTIONS } from "@/constants/roles";
import { stockAdminService } from "@/features/stocks/services/stock-admin.service";
import { stockService } from "@/features/stocks/services/stock.service";
import { stockAvailabilityService } from "@/features/stocks/services/stock-availability.service";
import { productService } from "@/features/products/services/product-api.service";
import type {
  StockResponseDTO,
  StockSearchParams,
} from "@/features/stocks/types/stock.types";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import {
  StockCondition,
  StockStatus,
} from "@/features/stocks/types/stock.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { dateToLocalDateString } from "@/shared/lib/date-format";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { toast } from "sonner";
import { StocksTable } from "@/features/stocks/components/StocksTable";
import { StockFormDialog } from "@/features/stocks/components/StockFormDialog";
import { StockDeleteDialog } from "@/features/stocks/components/StockDeleteDialog";
import { StockImageDialog } from "@/features/stocks/components/StockImageDialog";
import { StockCalendarDialog } from "@/features/stocks/components/StockCalendarDialog";
import {
  fileToBase64,
  fileToBase64WithPrefix,
  isValidImageFile,
  isValidFileSize,
} from "@/features/products/utils/product.utils";
import {
  getStockConditionLabel,
  getStockStatusLabel,
} from "@/features/stocks/utils/stock.utils";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  VIEW_IMAGE: "view-image",
  CALENDAR: "calendar",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

interface StockSearchFilters {
  barcode: string[];
  size: string[];
  condition: StockCondition[];
}

const stockSchema = z.object({
  productId: z.number().min(1, "Produto é obrigatório"),
  barcode: z.string().min(1, "Código de barras é obrigatório"),
  size: z.string().min(1, "Tamanho é obrigatório"),
  condition: z.enum([
    StockCondition.NEW,
    StockCondition.USED,
    StockCondition.WORN_OUT,
  ]),
  photo: z.string().optional(),
});

type StockForm = z.infer<typeof stockSchema>;

export function EstoquePage() {
  const { isAdmin } = useAuth();
  const [stocks, setStocks] = useState<StockResponseDTO[]>([]);
  const [products, setProducts] = useState<ProductResponseDTO[]>([]);
  const [stockStatuses, setStockStatuses] = useState<Map<number, StockStatus>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchFilters, setSearchFilters] = useState<StockSearchFilters>({
    barcode: [],
    size: [],
    condition: [],
  });
  const [barcodeInput, setBarcodeInput] = useState("");
  const [sizeInput, setSizeInput] = useState("");

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedStock, setSelectedStock] = useState<StockResponseDTO | null>(
    null,
  );
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const form = useForm<StockForm>({
    resolver: zodResolver(stockSchema),
    defaultValues: {
      productId: 0,
      barcode: "",
      size: "",
      condition: StockCondition.NEW,
      photo: "",
    },
  });

  const loadProducts = useCallback(async () => {
    try {
      const fetchProducts = isAdmin
        ? productService.list
        : productService.searchUser;
      const response = await fetchProducts({ page: 0, size: 1000 });
      setProducts(response.content);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar produtos"));
    }
  }, [isAdmin]);

  const loadStockStatuses = useCallback(async (stockIds: number[]) => {
    if (stockIds.length === 0) return;

    try {
      // Data local do usuário (não UTC) — evita consultar dia errado
      // após 21h em fusos negativos.
      const today = dateToLocalDateString(new Date());
      const availabilities = await stockAvailabilityService.getAvailability({
        stockIds,
        startDate: today,
        endDate: today,
      });

      const statusMap = new Map<number, StockStatus>();
      availabilities.forEach((availability) => {
        const todayStatus = availability.statusByDate[today];
        if (todayStatus) {
          statusMap.set(availability.stockId, todayStatus as StockStatus);
        }
      });

      setStockStatuses(statusMap);
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Erro ao carregar status dos estoques"),
      );
    }
  }, []);

  const loadStocks = useCallback(
    async (page: number = 0, filters?: StockSearchFilters) => {
      setLoading(true);
      try {
        const params: StockSearchParams = {
          page,
          pageSize,
          sortBy: "createdAt",
          sortDirection: "DESC",
        };

        if (filters) {
          if (filters.barcode.length > 0) {
            params.barcode = filters.barcode;
          }
          if (filters.condition.length > 0) {
            params.condition = filters.condition;
          }
          if (filters.size.length > 0) {
            params.size = filters.size;
          }
        }

        const response = await stockService.list(params);
        setStocks(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);

        const stockIds = response.content.map((stock) => stock.id);
        await loadStockStatuses(stockIds);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar estoque"));
      } finally {
        setLoading(false);
      }
    },
    [pageSize, loadStockStatuses],
  );

  useEffect(() => {
    // Mount only — carga inicial de produtos + estoques. Padrão legado
    // Família A; migrar para useStockList em refator futuro.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadProducts();
    loadStocks(0, { barcode: [], size: [], condition: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredStocks = useMemo(() => {
    if (!searchTerm) return stocks;
    const search = searchTerm.toLowerCase();
    return stocks.filter((stock) => {
      const stockStatus = stockStatuses.get(stock.id);
      const statusLabel = stockStatus
        ? getStockStatusLabel(stockStatus).toLowerCase()
        : "";
      const conditionLabel = getStockConditionLabel(
        stock.condition,
      ).toLowerCase();

      return (
        stock.barcode.toLowerCase().includes(search) ||
        stock.size.toLowerCase().includes(search) ||
        stock.productName.toLowerCase().includes(search) ||
        statusLabel.includes(search) ||
        conditionLabel.includes(search)
      );
    });
  }, [stocks, searchTerm, stockStatuses]);

  const handleOpenDialog = useCallback(
    (mode: DialogMode, stock?: StockResponseDTO) => {
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedStock(stock || null);

      if (mode === DIALOG_MODE.EDIT && stock) {
        form.reset({
          productId: stock.productId,
          barcode: stock.barcode,
          size: stock.size,
          condition: stock.condition,
          photo: "",
        });
        if (stock.photoUrl) {
          setPhotoPreview(
            `${stock.photoUrl}?t=${new Date(stock.updatedAt).getTime()}`,
          );
        }
        setPhotoRemoved(false);
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({
          productId: 0,
          barcode: "",
          size: "",
          condition: StockCondition.NEW,
          photo: "",
        });
        setPhotoPreview(null);
        setPhotoRemoved(false);
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setTimeout(() => {
      setDialogContent(null);
      setSelectedStock(null);
      setPhotoPreview(null);
      setPhotoRemoved(false);
      form.reset();
    }, 300);
  }, [form]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidImageFile(file)) {
        toast.error("Por favor, selecione apenas arquivos de imagem");
        return;
      }

      if (!isValidFileSize(file, 5)) {
        toast.error("A imagem deve ter no máximo 5MB");
        return;
      }

      try {
        const [base64Data, base64WithPrefix] = await Promise.all([
          fileToBase64(file),
          fileToBase64WithPrefix(file),
        ]);
        form.setValue("photo", base64Data);
        setPhotoPreview(base64WithPrefix);
        setPhotoRemoved(false);
      } catch {
        toast.error("Erro ao processar a imagem");
      }
    },
    [form],
  );

  const handleRemovePhoto = useCallback(() => {
    form.setValue("photo", "");
    setPhotoPreview(null);
    setPhotoRemoved(true);
  }, [form]);

  const handleViewImage = useCallback((stock: StockResponseDTO) => {
    setSelectedStock(stock);
    setDialogMode(DIALOG_MODE.VIEW_IMAGE);
    setDialogContent(DIALOG_MODE.VIEW_IMAGE);
  }, []);

  const handleViewCalendar = useCallback((stock: StockResponseDTO) => {
    setSelectedStock(stock);
    setDialogMode(DIALOG_MODE.CALENDAR);
    setDialogContent(DIALOG_MODE.CALENDAR);
  }, []);

  const onSubmit = useCallback(
    async (data: StockForm) => {
      setLoading(true);
      try {
        if (dialogMode === DIALOG_MODE.CREATE) {
          const newStock = await stockAdminService.create({
            productId: data.productId,
            barcode: data.barcode,
            size: data.size,
            condition: data.condition,
            photo: data.photo,
          });
          toast.success("Item criado com sucesso!");

          setStocks((prev) => [newStock, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedStock) {
          const photoValue = photoRemoved ? null : data.photo || "";

          const updatedStock = await stockAdminService.update(
            selectedStock.id,
            {
              productId: data.productId,
              barcode: data.barcode,
              size: data.size,
              condition: data.condition,
              photo: photoValue,
            },
          );
          toast.success("Item atualizado com sucesso!");

          setStocks((prevStocks) =>
            prevStocks.map((s) =>
              s.id === updatedStock.id ? updatedStock : s,
            ),
          );
        }
        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(getApiErrorMessage(error, "Erro ao salvar item"));
        }
      } finally {
        setLoading(false);
      }
    },
    [dialogMode, selectedStock, handleCloseDialog, photoRemoved, form],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedStock) return;

    setLoading(true);
    try {
      await stockAdminService.delete(selectedStock.id);
      toast.success("Item excluído com sucesso!");

      const updatedStocks = stocks.filter((s) => s.id !== selectedStock.id);
      setStocks(updatedStocks);

      const newTotalElements = totalElements - 1;
      setTotalElements(newTotalElements);

      const newTotalPages = Math.ceil(newTotalElements / pageSize);
      setTotalPages(newTotalPages);

      if (updatedStocks.length === 0 && currentPage > 0) {
        await loadStocks(currentPage - 1);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir item"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedStock,
    stocks,
    totalElements,
    pageSize,
    currentPage,
    handleCloseDialog,
    loadStocks,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadStocks(newPage, searchFilters);
      }
    },
    [totalPages, searchFilters, loadStocks],
  );

  const handleAddBarcode = useCallback(() => {
    if (
      barcodeInput.trim() &&
      !searchFilters.barcode.includes(barcodeInput.trim())
    ) {
      setSearchFilters((prev) => ({
        ...prev,
        barcode: [...prev.barcode, barcodeInput.trim()],
      }));
      setBarcodeInput("");
    }
  }, [barcodeInput, searchFilters.barcode]);

  const handleRemoveBarcode = useCallback((barcode: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      barcode: prev.barcode.filter((b) => b !== barcode),
    }));
  }, []);

  const handleAddSize = useCallback(() => {
    if (sizeInput.trim() && !searchFilters.size.includes(sizeInput.trim())) {
      setSearchFilters((prev) => ({
        ...prev,
        size: [...prev.size, sizeInput.trim()],
      }));
      setSizeInput("");
    }
  }, [sizeInput, searchFilters.size]);

  const handleRemoveSize = useCallback((size: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      size: prev.size.filter((s) => s !== size),
    }));
  }, []);

  const handleToggleCondition = useCallback((condition: StockCondition) => {
    setSearchFilters((prev) => {
      const hasCondition = prev.condition.includes(condition);
      return {
        ...prev,
        condition: hasCondition
          ? prev.condition.filter((c) => c !== condition)
          : [...prev.condition, condition],
      };
    });
  }, []);

  const handleApplyFilters = useCallback(() => {
    setCurrentPage(0);
    loadStocks(0, searchFilters);
  }, [searchFilters, loadStocks]);

  const handleClearFilters = useCallback(() => {
    setSearchFilters({ barcode: [], size: [], condition: [] });
    setBarcodeInput("");
    setSizeInput("");
    setCurrentPage(0);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Estoque</h1>
          <p className="text-muted-foreground">
            Gerencie o inventário de estoques
          </p>
        </div>
        <Can permission={ACTIONS.stock.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Novo Estoque
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Busque e filtre itens do estoque</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Códigos de Barras</label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Digite um código"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddBarcode();
                  }
                }}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddBarcode}
                disabled={!barcodeInput.trim()}
              >
                Adicionar
              </Button>
            </div>
            {searchFilters.barcode.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {searchFilters.barcode.map((code) => (
                  <Badge
                    key={code}
                    variant="secondary"
                    className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => handleRemoveBarcode(code)}
                  >
                    {code} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Tamanhos</label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Digite um tamanho"
                value={sizeInput}
                onChange={(e) => setSizeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSize();
                  }
                }}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddSize}
                disabled={!sizeInput.trim()}
              >
                Adicionar
              </Button>
            </div>
            {searchFilters.size.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {searchFilters.size.map((s) => (
                  <Badge
                    key={s}
                    variant="secondary"
                    className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => handleRemoveSize(s)}
                  >
                    {s} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Condições</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.values(StockCondition).map((condition) => (
                <Badge
                  key={condition}
                  variant={
                    searchFilters.condition.includes(condition)
                      ? "default"
                      : "outline"
                  }
                  className="cursor-pointer"
                  onClick={() => handleToggleCondition(condition)}
                >
                  {getStockConditionLabel(condition)}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:justify-between gap-2">
            <div className="flex items-center justify-center space-x-2">
              <Button onClick={handleApplyFilters} disabled={loading}>
                <Search className="w-4 h-4" />
                Buscar
              </Button>
              <Button
                variant="outline"
                onClick={handleClearFilters}
                disabled={
                  loading ||
                  (searchFilters.barcode.length === 0 &&
                    searchFilters.size.length === 0 &&
                    searchFilters.condition.length === 0)
                }
              >
                Limpar Filtros
              </Button>
            </div>
            <div className="flex items-center justify-center">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={loading}
              >
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
                <option value={100}>100 / página</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Inventário</CardTitle>
              <CardDescription>
                {loading
                  ? "Carregando..."
                  : `Mostrando ${filteredStocks.length} de ${totalElements} itens`}
              </CardDescription>
            </div>
            <Input
              placeholder="Filtrar na página atual..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="md:max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading && stocks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando estoque...
            </div>
          ) : filteredStocks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum item encontrado
            </div>
          ) : (
            <StocksTable
              stocks={filteredStocks}
              stockStatuses={stockStatuses}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onEdit={(stock) => handleOpenDialog(DIALOG_MODE.EDIT, stock)}
              onDelete={(stock) => handleOpenDialog(DIALOG_MODE.DELETE, stock)}
              onViewImage={handleViewImage}
              onViewCalendar={handleViewCalendar}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <StockFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        photoPreview={photoPreview}
        form={form}
        products={products}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        onFileChange={handleFileChange}
        onRemovePhoto={handleRemovePhoto}
      />

      <StockDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        stock={selectedStock}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />

      <StockImageDialog
        open={dialogMode === DIALOG_MODE.VIEW_IMAGE}
        stock={selectedStock}
        onClose={handleCloseDialog}
      />

      <StockCalendarDialog
        open={dialogMode === DIALOG_MODE.CALENDAR}
        stock={selectedStock}
        onClose={handleCloseDialog}
      />
    </div>
  );
}
